import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

function Pulse({ className }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-linea/60 ${className ?? ''}`}
      aria-hidden
    />
  );
}

/**
 * Route-aware skeleton while lazy chunks download.
 * Sized for use inside Layout's padded <main> (no extra page chrome).
 */
export function RouteFallback() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  let body: React.ReactNode;

  if (pathname === '/library') {
    body = (
      <div className="w-full" aria-hidden>
        <Pulse className="h-9 w-48" />
        <Pulse className="mt-6 h-24 rounded-card" />
        <div className="mt-4 space-y-2">
          <Pulse className="h-16 rounded-card" />
          <Pulse className="h-16 rounded-card" />
          <Pulse className="h-16 rounded-card" />
        </div>
      </div>
    );
  } else if (pathname.startsWith('/surgery/') || pathname.startsWith('/edit/')) {
    body = (
      <div className="mx-auto w-full max-w-[760px]" aria-hidden>
        <Pulse className="h-4 w-40" />
        <Pulse className="mt-4 h-12 w-56" />
        <Pulse className="mt-6 h-48 rounded-card" />
        <Pulse className="mt-4 h-32 rounded-card" />
      </div>
    );
  } else if (pathname === '/new') {
    body = (
      <div className="mx-auto w-full max-w-[900px]" aria-hidden>
        <Pulse className="h-8 w-52" />
        <Pulse className="mt-6 h-12 w-full max-w-md rounded-full" />
        <Pulse className="mt-8 h-64 rounded-card" />
      </div>
    );
  } else if (pathname === '/settings' || pathname === '/account') {
    body = (
      <div className="mx-auto w-full max-w-[760px]" aria-hidden>
        <Pulse className="h-8 w-36" />
        <Pulse className="mt-2 h-4 w-64" />
        <Pulse className="mt-8 h-40 rounded-card" />
        <Pulse className="mt-4 h-40 rounded-card" />
      </div>
    );
  } else if (pathname === '/procedures') {
    body = (
      <div className="mx-auto w-full max-w-3xl" aria-hidden>
        <Pulse className="h-4 w-28" />
        <Pulse className="mt-2 h-9 w-56" />
        <Pulse className="mt-2 h-4 w-80 max-w-full" />
        <Pulse className="mt-6 h-9 w-full max-w-sm" />
        <div className="mt-4 space-y-3">
          <Pulse className="h-24 rounded-card" />
          <Pulse className="h-24 rounded-card" />
          <Pulse className="h-24 rounded-card" />
        </div>
      </div>
    );
  } else {
    /* Dashboard / report default */
    body = (
      <div className="w-full" aria-hidden>
        <Pulse className="h-10 w-56" />
        <Pulse className="mt-2 h-5 w-72 max-w-full" />
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Pulse className="h-28 rounded-card" />
          <Pulse className="h-28 rounded-card" />
          <Pulse className="h-28 rounded-card" />
        </div>
        <Pulse className="mt-6 h-48 rounded-card" />
      </div>
    );
  }

  return (
    <div>
      <p className="sr-only" role="status" aria-live="polite">
        {t('common.loading')}
      </p>
      {body}
    </div>
  );
}
